import type { ChangeEvent } from 'react';
import type { FieldValues, FieldErrors, Path, UseFormRegister } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  USER_FIELD_MAX_LENGTHS,
  getUserFieldLabel,
  isNumericUserField,
  isRequiredUserField,
} from '../../constants/user.constants';

interface TextFieldProps<T extends FieldValues> {
  name: Path<T>;
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  type?: string;
}

export function TextField<T extends FieldValues>({
  name,
  register,
  errors,
  type = 'text',
}: TextFieldProps<T>) {
  const label = getUserFieldLabel(name);
  const error = errors[name];
  const numericOnly = isNumericUserField(name);
  const maxLength = USER_FIELD_MAX_LENGTHS[name];
  const required = isRequiredUserField(name);
  const registration = register(name);

  const handleChange = numericOnly
    ? (event: ChangeEvent<HTMLInputElement>) => {
        event.target.value = event.target.value.replace(/\D/g, '');
        return registration.onChange(event);
      }
    : registration.onChange;

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={name} className="gap-1">
        <span>
          {label}
          {required && <span className="text-destructive">*</span>}
        </span>
      </Label>
      <Input
        id={name}
        type={type}
        inputMode={numericOnly ? 'numeric' : undefined}
        maxLength={maxLength}
        placeholder={label}
        className="h-10"
        {...registration}
        onChange={handleChange}
      />
      {typeof error?.message === 'string' && (
        <p className="text-xs text-destructive">{error.message}</p>
      )}
    </div>
  );
}
